import { useMemo } from "react";
import { useGeographyLabel } from "../../../../../hooks/useGeographyLabel";
import { useIndustryLabel } from "../../../../../hooks/useIndustryLabel";
import { CompanyFilter } from "../../../../../legacy/types/companyFilter";
import { generateMinMaxLabel } from "../../../../../legacy/utils/spog";
import { CompanyMatchUploadResponse } from "../../../../../types/companyMatch";
import { useCompanyMatchLabel } from "./useCompanyMatchLabel";
import { useCompanyTypeLabel } from "./useCompanyTypeLabel";
import { useOpportunityTypeLabel } from "./useOpportunityTypeLabel";
import { useProductAttributesLabel } from "./useProductAttributesLabel";
import { useProductCategoriesLabel } from "./useProductCategoriesLabel";
import { useProductsLabel } from "./useProductsLabel";
import { useTargetOrganizationsLabel } from "./useTargetOrganizationsLabel";
import { useVendorsLabel } from "./useVendorsLabel";

type FiltersChipLabelMap = Record<string, string>;

interface FiltersChipLabelMapOptions {
  filters: CompanyFilter | undefined;
  draftFilters: CompanyFilter | undefined;
  uploadedAccountsProcessing?: CompanyMatchUploadResponse | null;
}

export const useFiltersChipLabelMap = (
  options: FiltersChipLabelMapOptions
): FiltersChipLabelMap => {
  const { filters, draftFilters, uploadedAccountsProcessing } = options;
  const companyMatch = useMemo(() => {
    // First check if there's a file being processed
    if (uploadedAccountsProcessing) return uploadedAccountsProcessing;

    if (filters?.companyMatch?.lastMatched) return filters.companyMatch;

    return draftFilters?.companyMatch;
  }, [
    filters?.companyMatch,
    draftFilters?.companyMatch,
    uploadedAccountsProcessing,
  ]);

  const companyMatchLabel = useCompanyMatchLabel(companyMatch);

  const opportunityType = useMemo(() => {
    return filters?.opportunityType || draftFilters?.opportunityType;
  }, [filters?.opportunityType, draftFilters?.opportunityType]);

  const opportunityTypeLabel = useOpportunityTypeLabel(opportunityType);

  const targetOrganizations = useTargetOrganizationsLabel({
    hgIds: filters?.hgIds,
    hgIdsIncludingCorporateRelatives: filters?.hgIdsIncludingCorporateRelatives,
  });

  const companyType = useCompanyTypeLabel(filters as CompanyFilter);

  const revenue = generateMinMaxLabel({
    value: {
      min: filters?.revenue?.min,
      max: filters?.revenue?.max,
    },
    isCurrency: true,
  });

  const employees = generateMinMaxLabel({
    value: {
      min: filters?.employees?.min,
      max: filters?.employees?.max,
    },
  });

  const globalPresence = filters?.globalPresence?.isMultiNational
    ? "Multi-National"
    : "Location";

  const productCategoriesLabel = useProductCategoriesLabel(
    filters?.productCategories?.ids || []
  );

  const productAttributesLabel = useProductAttributesLabel(
    filters?.productAttributes?.ids || []
  );

  const productsLabel = useProductsLabel(filters?.products);
  const vendorsLabel = useVendorsLabel(filters?.vendors);
  const industryLabel = useIndustryLabel(filters?.industry);
  const geographyLabel = useGeographyLabel({
    countryAlpha2s: filters?.countryAlpha2s,
    excludedCountryAlpha2s: filters?.excludedCountryAlpha2s,
    regionNames: filters?.regionNames,
    stateIds: filters?.stateIds,
  });

  return {
    companyMatch: companyMatchLabel,
    opportunityType: opportunityTypeLabel,
    targetOrganizations,
    companyType,
    productCategories: productCategoriesLabel,
    products: productsLabel,
    vendors: vendorsLabel,
    industry: industryLabel,
    geography: geographyLabel,
    revenue,
    employees,
    productAttributes: productAttributesLabel,
    globalPresence,
  };
};
